import type { FC } from 'react';
import { Button, FormHelperText } from '@selfinspectionorg/ui-kit';
import OTPInput from 'react18-input-otp';
import styles from './Mfa.module.scss';

export const EMAIL_CODE_LENGTH = 6;

interface Props {
  code: string;
  onCodeChange: (code: string) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  error?: string | null;
  /** Resend control; omitted (with `isResending`) when the flow offers no in-place resend. */
  onResend?: () => void;
  isResending?: boolean;
  /** Seconds remaining in the resend cooldown; while > 0 the resend button is disabled. */
  resendCountdown?: number;
}

/**
 * Presentational core of the email one-time-code step: the code field, confirm, and an optional
 * resend. Owns no data-fetching — the login-time ceremony and the session-authorized Security-page
 * setup both drive it, supplying how the code is submitted and (only where it applies) resent.
 */
export const EmailStepView: FC<Props> = ({
  code,
  onCodeChange,
  onSubmit,
  isSubmitting,
  error,
  onResend,
  isResending = false,
  resendCountdown = 0,
}) => (
  <div className={styles.body}>
    <div className={styles.codeInputWrapper}>
      <OTPInput
        value={code}
        onChange={onCodeChange}
        numInputs={EMAIL_CODE_LENGTH}
        containerStyle={styles.codeInputWrapper}
        inputStyle={styles.codeInput}
        isInputNum
        shouldAutoFocus
        isDisabled={isSubmitting}
        data-testid="mfa-email-code-input"
      />
    </div>
    <Button
      width="100%"
      onClick={onSubmit}
      disabled={code.length !== EMAIL_CODE_LENGTH || isSubmitting}
      isLoading={isSubmitting}
      data-testid="mfa-email-confirm-button"
    >
      Confirm
    </Button>
    {error && <FormHelperText errored>{error}</FormHelperText>}
    {onResend && (
      <div className={styles.resendWrapper}>
        <span className={styles.resendText}>Didn't receive the code?</span>
        <button
          type="button"
          className={styles.resendButton}
          onClick={onResend}
          disabled={isResending || isSubmitting || resendCountdown > 0}
          data-testid="mfa-email-resend-button"
        >
          {resendCountdown > 0 ? `Resend code (${resendCountdown}s)` : 'Resend code'}
        </button>
      </div>
    )}
  </div>
);
